import { useParams, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, Calendar, Clock } from "lucide-react";
import { API, IMG_LUMBA, imgSrc, imageRatioClass } from "@/lib/api";
import { usePageMeta } from "@/lib/seo";

const BG = ["blog-bg-pp-pe", "blog-bg-brand", "blog-bg-katrol", "blog-bg-timbangan"];

export const parseContent = (text) => {
  const lines = (text || "").split("\n");
  const blocks = [];
  let list = null;
  let table = null;
  let k = 0;
  const flush = () => {
    if (list) { blocks.push({ t: "ul", items: list, k: k++ }); list = null; }
    if (table) { blocks.push({ t: "table", rows: table, k: k++ }); table = null; }
  };
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) { flush(); continue; }
    if (line.startsWith("## ")) {
      flush();
      blocks.push({ t: "h", text: line.slice(3), k: k++ });
    } else if (line.startsWith("- ")) {
      if (table) flush();
      (list = list || []).push(line.slice(2));
    } else if (line.startsWith("|")) {
      if (list) flush();
      const cells = line.split("|").map((c) => c.trim()).filter((_, i, a) => i > 0 && i < a.length - 1);
      (table = table || []).push(cells);
    } else {
      flush();
      blocks.push({ t: "p", text: line, k: k++ });
    }
  }
  flush();
  return blocks;
};

export default function BlogDetail() {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [site, setSite] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setBlog(null);
    setNotFound(false);
    API.get(`/blog/${slug}`).then((r) => setBlog(r.data)).catch(() => setNotFound(true));
    API.get("/settings").then((r) => setSite(r.data)).catch(() => {});
  }, [slug]);

  usePageMeta(blog?.title || "Blog", blog?.excerpt || "Artikel panduan produk NAWADAH by NDH Official.", {
    type: blog ? "article" : "website",
    image: blog?.image ? imgSrc(blog.image) : undefined,
    publishedTime: blog?.created_at,
    noindex: notFound,
  });

  if (notFound) {
    return (
      <div className="min-h-screen bg-navy-dark text-white flex flex-col items-center justify-center gap-4" data-testid="blog-not-found">
        <p className="text-slate-400">Artikel tidak ditemukan.</p>
        <Link to="/blog" data-testid="blog-back" className="text-gold font-semibold hover:underline">Kembali ke Blog</Link>
      </div>
    );
  }
  if (!blog) {
    return <div className="min-h-screen bg-navy-dark text-white flex items-center justify-center text-slate-400">Memuat artikel...</div>;
  }

  const bgClass = BG[Math.abs([...String(blog.slug)].reduce((a, c) => a + c.charCodeAt(0), 0)) % BG.length];
  const blocks = parseContent(blog.content);
  const dLayout = site?.blog_detail_layout || "klasik";

  const imgBlock = (big = false) => (
    <div className={`rounded-md overflow-hidden border border-gold/20 bg-white ${imageRatioClass(blog.image_ratio, "16:9")} ${big ? "mb-10 shadow-2xl" : "mb-12"}`}>
      <img
        src={blog.image ? imgSrc(blog.image) : IMG_LUMBA}
        alt={blog.title}
        className="w-full h-full object-cover"
        data-testid="blog-hero-image"
      />
    </div>
  );

  const headerText = (
    <>
      <p className="text-xs font-bold uppercase tracking-[0.3em] text-gold mb-4">Blog NAWADAH</p>
      <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight mb-4" data-testid="blog-title">{blog.title}</h1>
      <p className="text-xs text-slate-400 flex items-center gap-4 mb-8">
        <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5 text-gold" /> {blog.date_label}</span>
        <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-gold" /> {blog.read_time || "5 menit baca"}</span>
      </p>
    </>
  );

  return (
    <div className={`relative min-h-screen text-white noise-overlay ${bgClass}`} data-testid="blog-detail">
      <div className={`mx-auto px-4 sm:px-6 py-14 ${dLayout === "sinematik" ? "max-w-4xl" : "max-w-3xl"}`}>
        <Link to="/blog" data-testid="blog-back" className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-gold transition-colors mb-10">
          <ArrowLeft className="h-4 w-4" /> Semua Artikel
        </Link>

        {dLayout === "sinematik" && imgBlock(true)}
        {headerText}
        {dLayout === "klasik" && imgBlock(false)}

        <article className={`space-y-10 ${dLayout === "sinematik" ? "max-w-3xl mx-auto" : ""}`} data-testid="blog-content">
          {blocks.map((b, i) => {
            if (b.t === "h") {
              return (
                <h2 key={b.k} className="font-display text-xl sm:text-2xl text-white flex items-start gap-3" data-testid={`blog-section-${i}`}>
                  <span className="text-gold font-mono-accent text-sm mt-1.5">{String(i + 1).padStart(2, "0")}</span>
                  {b.text}
                </h2>
              );
            }
            if (b.t === "ul") {
              return (
                <ul key={b.k} className="space-y-2.5" data-testid={`blog-list-${i}`}>
                  {b.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-3 text-sm sm:text-base text-slate-300 leading-relaxed">
                      <span className="mt-2 h-1.5 w-1.5 rounded-full bg-gold shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              );
            }
            if (b.t === "table") {
              const [head, ...rows] = b.rows;
              return (
                <div key={b.k} className="overflow-x-auto my-6" data-testid={`blog-table-${i}`}>
                  <table className="w-full text-sm border border-white/10 rounded-md overflow-hidden">
                    <thead>
                      <tr>
                        {head.map((h, j) => (
                          <th key={j} className="bg-navy text-gold text-left px-4 py-3 border border-white/10 font-bold text-xs uppercase tracking-wider">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, j) => (
                        <tr key={j} className={j % 2 === 0 ? "bg-white/[0.02]" : ""}>
                          {row.map((cell, x) => (
                            <td key={x} className={`px-4 py-3 border border-white/10 leading-relaxed ${x === 0 ? "font-semibold text-white" : "text-slate-300"}`}>{cell}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            }
            return <p key={b.k} className="text-sm sm:text-base text-slate-300 leading-relaxed" data-testid={`blog-para-${i}`}>{b.text}</p>;
          })}
        </article>

        <div className={`mt-16 card-maritime-surface rounded-md p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 ${dLayout === "sinematik" ? "max-w-3xl mx-auto" : ""}`} data-testid="blog-cta">
          <div>
            <h3 className="font-display text-xl text-white mb-2">Tertarik dengan Produknya?</h3>
            <p className="text-sm text-slate-400">Lihat katalog produk NAWADAH atau tanya langsung via WhatsApp.</p>
          </div>
          <Link to="/produk" data-testid="blog-cta-catalog" className="shrink-0 inline-flex items-center gap-2 px-8 py-3.5 bg-gold bg-gold-hover text-[#0B1F3A] font-bold text-sm rounded-md transition-colors">
            Lihat Katalog <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
